import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AnswerValue, SurveyQuestion } from 'shared'
import { validateSubmission } from 'shared'
import { api, ApiError } from '../../utils/api'
import { withSeedRows } from './tableAnswers'
import { toErrorMessage } from '../../lib/errors'

interface UseSurveyFormOptions {
  workspaceId: string
  surveyId: string
  questions: SurveyQuestion[]
  initialAnswers: Record<string, AnswerValue>
  submitted: boolean
  /**
   * Nothing is saved along the way and everything is sent at once. A partial
   * save is an answer held for a person, which is what an anonymous survey
   * must not have.
   */
  anonymous?: boolean
  /**
   * Someone else is filling in the answer and the server refused this one. What was
   * waiting to be saved is dropped (it could not be written), and `lost` says whether
   * there was any, so the page can say the last changes did not go through.
   */
  onLocked?: (holder: { id: string; name: string } | null, lost: boolean) => void
}

const AUTOSAVE_DELAY_MS = 1500

function tableAnswers(
  questions: SurveyQuestion[],
  answers: Record<string, AnswerValue>,
): Record<string, AnswerValue> {
  return Object.fromEntries(
    questions
      .filter((question) => question.type === 'table' && answers[question.id] !== undefined)
      .map((question) => [question.id, answers[question.id]]),
  )
}

/**
 * Holds the answers being typed, saves them quietly in the background, and
 * validates with the same rule the API uses.
 *
 * Errors appear only after a submit attempt: flagging a required field the
 * moment someone tabs past it makes a half-finished form look broken, and this
 * one is meant to be filled in over several sittings.
 */
export function useSurveyForm({
  workspaceId,
  surveyId,
  questions,
  initialAnswers,
  submitted,
  anonymous = false,
  onLocked,
}: UseSurveyFormOptions) {
  const { t } = useTranslation()
  // A table nobody has touched still shows the author's rows, so it validates
  // and is sent as what the person is looking at.
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>(() =>
    withSeedRows(questions, initialAnswers),
  )
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const pending = useRef<Record<string, AnswerValue>>({})
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const errors = useMemo(() => {
    const found = validateSubmission(questions, answers)
    return Object.fromEntries(
      found.map((entry) => {
        const message = t(`survey.errors.${entry.code}`, { ...entry.params })
        return [
          entry.question_id,
          // A table has many cells: say which one, since the page shows one message.
          entry.cell?.column
            ? t('survey.errors.tableCell', {
                row: entry.cell.row_id,
                column:
                  questions
                    .find((question) => question.id === entry.question_id)
                    ?.config?.columns?.find((column) => column.key === entry.cell?.column)?.label ??
                  entry.cell.column,
                message,
              })
            : entry.cell?.row_id
              ? t('survey.errors.tableRow', { row: entry.cell.row_id, message })
              : message,
        ]
      }),
    )
  }, [questions, answers, t])

  const flush = useCallback(async () => {
    if (anonymous) return

    const batch = pending.current
    if (Object.keys(batch).length === 0) return

    pending.current = {}
    setSaving(true)

    try {
      await api.put(`/api/workspaces/${workspaceId}/surveys/${surveyId}/answers`, {
        answers: batch,
      })
      setSavedAt(new Date().toISOString())
      setError(null)
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        // Refused for good, not for now: writing it later would overwrite what the
        // other person is doing. The page tells the person, and shows what is there.
        onLocked?.((err.body?.holder as { id: string; name: string } | undefined) ?? null, true)
        setError(null)
        return
      }
      // Put the batch back so the next save retries it rather than losing it.
      pending.current = { ...batch, ...pending.current }
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }, [workspaceId, surveyId, anonymous, onLocked, t])

  const setAnswer = useCallback(
    (questionId: string, value: AnswerValue) => {
      if (submitted) return

      setAnswers((current) => ({ ...current, [questionId]: value }))
      if (anonymous) return

      pending.current[questionId] = value

      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(flush, AUTOSAVE_DELAY_MS)
    },
    [flush, submitted, anonymous],
  )

  // Leaving the page with keystrokes still queued would lose them.
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
      void flush()
    }
  }, [flush])

  const submit = useCallback(async () => {
    setShowErrors(true)

    if (Object.keys(errors).length > 0) {
      setError(t('survey.fixErrors'))
      return false
    }

    if (timer.current) clearTimeout(timer.current)
    setSubmitting(true)
    setError(null)

    try {
      // Send whatever is still queued with the submit itself, so nothing
      // depends on the autosave having landed first.
      await api.post(`/api/workspaces/${workspaceId}/surveys/${surveyId}/submit`, {
        // Tables go with every submit, whole: an untouched one still holds the
        // author's rows, which exist nowhere on the server until they are sent.
        answers: anonymous ? answers : { ...pending.current, ...tableAnswers(questions, answers) },
      })
      pending.current = {}
      return true
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        onLocked?.((err.body?.holder as { id: string; name: string } | undefined) ?? null, true)
        return false
      }
      setError(toErrorMessage(err) ?? t('errors.generic'))
      return false
    } finally {
      setSubmitting(false)
    }
  }, [errors, answers, anonymous, workspaceId, surveyId, questions, onLocked, t])

  /** Takes the answers as the server has them, for a person who has been reading while someone else worked. */
  const replaceAnswers = useCallback(
    (fresh: Record<string, AnswerValue>) => {
      if (timer.current) clearTimeout(timer.current)
      pending.current = {}
      setAnswers(withSeedRows(questions, fresh))
    },
    [questions],
  )

  return {
    answers,
    setAnswer,
    errors: showErrors ? errors : {},
    saving,
    savedAt,
    submitting,
    error,
    submit,
    replaceAnswers,
  }
}
